/**
 * The claims list's bulk reminder row, in the Pricing-page pattern:
 * "N selected / Clear / Turn reminders on / Turn reminders off", with a
 * confirm that says how many will change and how many are skipped.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { type ClaimRow, useBulkClaimReminders } from '@/admin/hooks/use-claims'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'

export function ClaimsReminderBulkBar({
  rows,
  onClear,
}: {
  rows: ClaimRow[]
  onClear: () => void
}) {
  const { t } = useTranslation()
  const bulk = useBulkClaimReminders()
  // true: turning on, false: turning off, null: no confirm open.
  const [turning, setTurning] = useState<boolean | null>(null)

  // The backend's rule: only claims waiting on documents and not already so.
  const willChange =
    turning === null ? 0 : rows.filter((row) => row.reminder?.off === turning).length

  const confirm = () => {
    if (turning === null) return
    bulk.mutate(
      { uuids: rows.map((row) => row.uuid), enabled: turning },
      {
        onSuccess: (result) => {
          toast.success(
            turning
              ? t('claims.bulkReminders.onDone', 'Reminders turned on: {{changed}} changed, {{skipped}} skipped.', result)
              : t('claims.bulkReminders.offDone', 'Reminders turned off: {{changed}} changed, {{skipped}} skipped.', result),
          )
          setTurning(null)
          onClear()
        },
        onError: (err) =>
          toastApiError(err, t('claims.bulkReminders.failed', 'Could not change the reminders')),
      },
    )
  }

  return (
    <div
      className="mb-3 flex flex-wrap items-center justify-between gap-3"
      data-testid="claims-reminder-bulk-bar"
    >
      <p className="text-[13px] text-[var(--ink-3)]">
        {t('claims.bulkReminders.selected', '{{count}} selected', { count: rows.length })}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClear}>
          {t('common.clear', 'Clear')}
        </Button>
        <Button variant="outline" onClick={() => setTurning(true)} data-testid="claims-reminders-on">
          {t('claims.bulkReminders.turnOn', 'Turn reminders on')}
        </Button>
        <Button variant="outline" onClick={() => setTurning(false)} data-testid="claims-reminders-off">
          {t('claims.bulkReminders.turnOff', 'Turn reminders off')}
        </Button>
      </div>

      {/* Conditionally mounted: Base UI dialogs don't reliably close on an
          always-mounted `open` toggle. */}
      {turning !== null && (
        <Dialog open onOpenChange={(open) => !open && setTurning(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {turning
                  ? t('claims.bulkReminders.onTitle', 'Turn on reminders?')
                  : t('claims.bulkReminders.offTitle', 'Turn off reminders?')}
              </DialogTitle>
              <DialogDescription>
                {t('claims.bulkReminders.willChange', '{{changed}} of the {{selected}} selected will change.', {
                  changed: willChange,
                  selected: rows.length,
                })}
                {willChange < rows.length &&
                  ` ${
                    turning
                      ? t('claims.bulkReminders.skippedOn', 'The rest are not waiting on documents, or already have reminders on.')
                      : t('claims.bulkReminders.skippedOff', 'The rest are not waiting on documents, or already have reminders off.')
                  }`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setTurning(null)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant={turning ? 'default' : 'destructive'}
                onClick={confirm}
                disabled={bulk.isPending}
                data-testid="claims-reminders-confirm"
              >
                {turning
                  ? t('claims.bulkReminders.confirmOn', 'Turn on')
                  : t('claims.bulkReminders.confirmOff', 'Turn off')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
